import React from 'react';
import {
  Switch,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  Alert,
  Snackbar,
  Button,
} from '@mui/material';
import { Play, Info, CheckCircle, XCircle } from 'lucide-react';
import { useNotify } from 'react-admin';
import type { Cron } from '@shared/types/automation';
import { updateCron, triggerCron } from '../../../data/cron';

interface CronJobsConfigProps {
  crons: Cron[];
  disabled?: boolean;
  onRefresh: () => Promise<void>;
}

// Utility to format relative time
const formatRelativeTime = (timestamp: string | null): string => {
  if (!timestamp) return 'Never';

  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

// Utility to format frequency as hours or days
const formatFrequencyHelper = (minutes: number): string => {
  if (minutes >= 1440) {
    // Show in days
    const days = minutes / 1440;
    const formatted = days % 1 === 0 ? days.toFixed(0) : days.toFixed(1);
    return `${formatted}d`;
  } else if (minutes >= 60) {
    // Show in hours
    const hours = minutes / 60;
    const formatted = hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1);
    return `${formatted}h`;
  }
  return ''; // Don't show helper for < 60 minutes
};

interface PendingChanges {
  [cronName: string]: {
    enabled?: boolean;
    frequencyMinutes?: number;
  };
}

export const CronJobsConfig: React.FC<CronJobsConfigProps> = ({
  crons,
  disabled = false,
  onRefresh,
}) => {
  const [triggeringJob, setTriggeringJob] = React.useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = React.useState<PendingChanges>(
    {}
  );
  const [saving, setSaving] = React.useState(false);
  const [notification, setNotification] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const notify = useNotify();

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const handleToggleEnabled = (cron: Cron) => {
    const currentValue = pendingChanges[cron.name]?.enabled ?? cron.enabled;
    setPendingChanges(prev => ({
      ...prev,
      [cron.name]: {
        ...prev[cron.name],
        enabled: !currentValue,
      },
    }));
  };

  const handleUpdateFrequency = (cron: Cron, newFrequency: number) => {
    setPendingChanges(prev => ({
      ...prev,
      [cron.name]: {
        ...prev[cron.name],
        frequencyMinutes: newFrequency,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Apply all pending changes
      for (const [cronName, changes] of Object.entries(pendingChanges)) {
        await updateCron(cronName, changes);
      }

      await onRefresh(); // Reload data
      setPendingChanges({}); // Clear pending changes
      notify('Cron jobs updated successfully', { type: 'success' });
    } catch (error) {
      notify(
        `Failed to update cron jobs: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingChanges({});
  };

  const handleTriggerJob = async (cron: Cron) => {
    try {
      setTriggeringJob(cron.name);
      await triggerCron(cron.name);
      notify(`${cron.name} triggered successfully`, { type: 'success' });

      // Refresh immediately to show updated last_run_at
      await onRefresh();
    } catch (error) {
      notify(
        `Failed to trigger ${cron.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );
      // Refresh to show error status in the table
      await onRefresh();
    } finally {
      setTriggeringJob(null);
    }
  };

  const closeNotification = () => {
    setNotification(null);
  };

  // Sort crons by name for consistent display
  const sortedCrons = [...crons].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedCrons.map(cron => {
          const isTriggering = triggeringJob === cron.name;
          const pendingCron = pendingChanges[cron.name];
          const displayEnabled = pendingCron?.enabled ?? cron.enabled;
          const displayFrequency =
            pendingCron?.frequencyMinutes ?? cron.frequencyMinutes;
          const lastRunTime = formatRelativeTime(cron.lastRunAt);
          const frequencyHelper = formatFrequencyHelper(displayFrequency);

          // Status icon based on lastSuccess
          let statusIcon = null;
          if (cron.lastRunAt) {
            if (cron.lastSuccess) {
              statusIcon = (
                <CheckCircle size={16} style={{ color: '#4caf50' }} />
              );
            } else {
              statusIcon = (
                <Tooltip title={cron.lastError || 'Unknown error'}>
                  <XCircle size={16} style={{ color: '#f44336' }} />
                </Tooltip>
              );
            }
          }
          // No icon for "never run" - statusIcon stays null

          return (
            <Box
              key={cron.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: pendingCron ? 'action.hover' : 'transparent',
              }}
            >
              {/* Toggle switch (moved to left) */}
              <Switch
                checked={displayEnabled}
                onChange={() => handleToggleEnabled(cron)}
                disabled={disabled || saving}
                size="small"
              />

              {/* Job info */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, minWidth: 180 }}
                >
                  {cron.name}
                </Typography>
                <Tooltip title={cron.description}>
                  <IconButton size="small" sx={{ color: 'text.secondary' }}>
                    <Info size={16} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Status and last run */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 200,
                }}
              >
                {statusIcon}
                <Typography variant="caption" color="text.secondary">
                  {lastRunTime}
                </Typography>
              </Box>

              {/* Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  type="number"
                  value={displayFrequency}
                  onChange={e => {
                    const newFreq = parseInt(e.target.value, 10);
                    if (newFreq && newFreq > 0) {
                      handleUpdateFrequency(cron, newFreq);
                    }
                  }}
                  disabled={disabled || !displayEnabled || saving}
                  size="small"
                  sx={{ width: 80 }}
                  inputProps={{ min: 1, max: 10080 }}
                />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 80 }}
                >
                  min{frequencyHelper && ` (${frequencyHelper})`}
                </Typography>

                <Tooltip title={`Trigger ${cron.name} now`}>
                  <IconButton
                    onClick={() => handleTriggerJob(cron)}
                    disabled={
                      disabled || !displayEnabled || isTriggering || saving
                    }
                    size="small"
                    sx={{ color: 'primary.main' }}
                  >
                    {isTriggering ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Play size={16} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Save/Cancel buttons */}
      {hasChanges && (
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || disabled}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={saving || disabled}
          >
            Cancel
          </Button>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        <strong>Note:</strong> Dispatcher runs every 5 minutes to check for due
        jobs.
      </Typography>

      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={closeNotification}
      >
        <Alert
          onClose={closeNotification}
          severity={notification?.type}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
